// "Favorites ▾" header dropdown — save the current configuration and browse /
// load saved ones (CPQ 1's Favorites). Configs are public by default (2.1.1);
// the browse view toggles My / All (default My, 2.1.3) and supports search + sort
// over Name / Customer / Part # / Origin / Created by / Created date (2.4).

import { useCallback, useEffect, useRef, useState } from "react";
import { useAnswers } from "../state/AnswersContext";
import { getLaunchContext } from "../util/launchContext";
import {
  saveFavorite,
  listFavorites,
  getFavorite,
  deleteFavorite,
  type SavedConfigMeta,
} from "../util/favoritesClient";

type View = null | "save" | "browse";
type Scope = "mine" | "all";

const COLUMNS: Array<{ key: string; label: string; sortable: boolean }> = [
  { key: "name", label: "Name", sortable: true },
  { key: "customer_name", label: "Customer", sortable: true },
  { key: "customer_part_number", label: "Part #", sortable: true },
  { key: "origin_type", label: "Origin", sortable: true },
  { key: "created_by_name", label: "Created by", sortable: true },
  { key: "created_at", label: "Created", sortable: true },
];

export default function FavoritesMenu() {
  const { answers, customerPartNumber, getConfigSnapshot, loadItemConfig, forceNewConfig, applyCustomerPartNumber, product } =
    useAnswers();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>(null);

  return (
    <div className="cpq-audit-wrap">
      <button
        type="button"
        className="cpq-audit-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        Favorites <span aria-hidden>▾</span>
      </button>

      {open && (
        <>
          <div className="cpq-audit-backdrop" onClick={() => setOpen(false)} />
          <ul className="cpq-audit-menu" role="menu">
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className="cpq-audit-menu-item"
                onClick={() => { setView("save"); setOpen(false); }}
              >
                Save Configuration…
              </button>
            </li>
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className="cpq-audit-menu-item"
                onClick={() => { setView("browse"); setOpen(false); }}
              >
                Browse / Load…
              </button>
            </li>
          </ul>
        </>
      )}

      {view && (
        <div
          className="cpq-audit-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setView(null); }}
        >
          <div className="cpq-audit-modal">
            <header className="cpq-audit-modal-head">
              <h2>{view === "save" ? "Save Configuration" : "Saved Configurations"}</h2>
              <button type="button" className="cpq-audit-close" aria-label="Close" onClick={() => setView(null)}>
                ✕
              </button>
            </header>
            <div className="cpq-audit-modal-body">
              {view === "save" ? (
                <SaveView
                  answers={answers}
                  customerPartNumber={customerPartNumber}
                  getConfigSnapshot={getConfigSnapshot}
                  product={product}
                  onDone={() => setView(null)}
                />
              ) : (
                <BrowseView
                  loadItemConfig={loadItemConfig}
                  forceNewConfig={forceNewConfig}
                  applyCustomerPartNumber={applyCustomerPartNumber}
                  product={product}
                  onLoaded={() => setView(null)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Save ----
function SaveView({
  answers,
  customerPartNumber,
  getConfigSnapshot,
  product,
  onDone,
}: {
  answers: Record<string, unknown>;
  customerPartNumber: string;
  getConfigSnapshot: () => Promise<string>;
  /** The running configurator's slug, stored so the lists can be scoped to it. */
  product: string;
  onDone: () => void;
}) {
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [phase, setPhase] = useState<"idle" | "saving" | "done" | "error">("idle");
  const [message, setMessage] = useState("");

  const submit = async () => {
    if (!name.trim()) { setMessage("Name is required."); setPhase("error"); return; }
    setPhase("saving"); setMessage("");
    try {
      const snapshot = await getConfigSnapshot();
      const ctx = getLaunchContext();
      const model = String(answers["HOUSING_VALIDATION/HOUSING_VALIDATION"] ?? "").trim() || null;
      await saveFavorite({
        name: name.trim(),
        notes: notes.trim() || null,
        configSnapshot: snapshot,
        model,
        product,
        customerId: ctx.customerId,
        customerName: ctx.customerName,
        customerPartNumber: customerPartNumber.trim() || null,
        originType: ctx.originType,
        originTranid: ctx.transactionId,
        createdById: ctx.userId,
        createdByName: ctx.userName,
      });
      setPhase("done");
      setMessage("Saved.");
      window.setTimeout(onDone, 700);
    } catch (e) {
      setPhase("error");
      setMessage(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="cpq-audit-card">
      <label className="cpq-fav-label">Configuration Name</label>
      <input
        className="cpq-input"
        value={name}
        autoFocus
        placeholder={'e.g. Titan 20" O-Type AC'}
        onChange={(e) => setName(e.target.value)}
      />
      <label className="cpq-fav-label" style={{ marginTop: "0.6rem" }}>Notes</label>
      <textarea
        className="cpq-input"
        rows={5}
        value={notes}
        placeholder="Optional notes…"
        onChange={(e) => setNotes(e.target.value)}
      />
      <div className="cpq-fav-actions">
        <button
          type="button"
          className="cpq-btn cpq-btn-primary"
          disabled={phase === "saving"}
          onClick={submit}
        >
          {phase === "saving" ? "Saving…" : "Save"}
        </button>
        {message && (
          <span className={phase === "error" ? "cpq-fav-err" : "cpq-fav-ok"}>{message}</span>
        )}
      </div>
    </div>
  );
}

// ---- Browse / Load ----
function BrowseView({
  loadItemConfig,
  forceNewConfig,
  applyCustomerPartNumber,
  product,
  onLoaded,
}: {
  loadItemConfig: (configJson: string) => Promise<{ count: number }>;
  forceNewConfig: () => void;
  applyCustomerPartNumber: (value: string | null) => void;
  /**
   * The running configurator's slug. BOTH "My Configurations" and "All
   * Configurations" are limited to it: a question CODE is only unique within a
   * product, so another product's configuration cannot be loaded meaningfully.
   */
  product: string;
  onLoaded: () => void;
}) {
  const ctx = getLaunchContext();
  const [scope, setScope] = useState<Scope>("mine"); // default My (2.1.3)
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("created_at");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [rows, setRows] = useState<SavedConfigMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  // Sequence guard: ignore a slow earlier response arriving after a newer one.
  const reqSeq = useRef(0);

  const fetchRows = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true); setError("");
    try {
      const items = await listFavorites({
        scope,
        product,
        createdBy: ctx.userId,
        q: q.trim() || undefined,
        sort,
        dir,
      });
      if (seq !== reqSeq.current) return; // a newer request superseded this one
      setRows(items);
    } catch (e) {
      if (seq !== reqSeq.current) return;
      setError(e instanceof Error ? e.message : String(e));
      setRows([]);
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
    // `product` belongs here even though it cannot change within a session: leaving it out
    // is what makes a filter silently stop being applied if this ever mounts under a
    // different configurator, and the linter would not catch a missing dep on a value that
    // happens to be constant today.
  }, [scope, product, ctx.userId, q, sort, dir]);

  useEffect(() => {
    const t = window.setTimeout(fetchRows, 200); // debounce search typing
    return () => window.clearTimeout(t);
  }, [fetchRows]);

  const toggleSort = (col: string) => {
    if (sort === col) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(col); setDir("asc"); }
  };

  const load = async (id: number) => {
    setLoadingId(id); setError("");
    try {
      // product is passed as a GUARD: the server refuses a row saved in another
      // configurator rather than letting its answers land here meaninglessly.
      const full = await getFavorite(id, product);
      await loadItemConfig(full.config_snapshot);
      // Source the saved configuration's Customer Part Number, or CLEAR the field
      // when it had none. Leaving the previous configuration's number behind would
      // claim it away from the part it belongs to on save.
      applyCustomerPartNumber(full.customer_part_number ?? null);
      // Loading a saved configuration switches the UI to New Config.
      //
      // NOTE, 2026-08-27: this NO LONGER decides what Save & Submit does. The server keeps
      // its own rule — a launched, unlocked, same-base item is edited, never duplicated —
      // and it wins over this. So loading a Favorite on top of an Edit Config launch in the
      // SAME family now OVERWRITES the launched part rather than making a new one.
      //
      // That is Kevin's call, made with the trade-off in front of him: "the base is still
      // BT-MB54/54-30 … then it will never go to a -2" outranks the risk of rewriting a
      // configuration, because the walking part number was the thing actually hurting.
      // Review raised this as critical; it is deliberate, not an oversight.
      // See the backstop in api/create-assembly/index.js.
      forceNewConfig();
      onLoaded();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setLoadingId(null);
    }
  };

  const del = async (id: number) => {
    if (!window.confirm("Delete this saved configuration? This can't be undone.")) return;
    setDeletingId(id); setError("");
    try {
      await deleteFavorite(id);
      setRows((rs) => rs.filter((r) => r.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <div className="cpq-fav-toolbar">
        <div className="cpq-fav-toggle" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={scope === "mine"}
            className={scope === "mine" ? "is-active" : ""}
            onClick={() => setScope("mine")}
          >
            My Configurations
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={scope === "all"}
            className={scope === "all" ? "is-active" : ""}
            onClick={() => setScope("all")}
          >
            All Configurations
          </button>
        </div>
        <input
          className="cpq-input cpq-fav-search"
          placeholder="Search name, customer, part #, origin…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {scope === "mine" && !ctx.userId && (
        <p className="cpq-audit-note">
          No signed-in user detected — “My Configurations” is empty until cpq2 is
          launched from the updated NetSuite button (which passes the operator).
        </p>
      )}
      {error && <p className="cpq-fav-err">{error}</p>}

      <div className="cpq-audit-scroll">
        <table className="cpq-table cpq-audit-table">
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  onClick={c.sortable ? () => toggleSort(c.key) : undefined}
                  style={{ cursor: c.sortable ? "pointer" : "default", whiteSpace: "nowrap" }}
                >
                  {c.label}{sort === c.key ? (dir === "asc" ? " ▲" : " ▼") : ""}
                </th>
              ))}
              <th aria-label="Delete" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={COLUMNS.length + 1}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={COLUMNS.length + 1}>No saved configurations.</td></tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.id}
                  className="cpq-fav-row"
                  onClick={() => load(r.id)}
                  title={r.notes || "Click to load"}
                >
                  <td>
                    {loadingId === r.id ? "Loading… " : ""}
                    {r.name}
                    {r.model ? <span className="cpq-fav-model"> · {r.model}</span> : null}
                  </td>
                  <td>{r.customer_name || r.customer_id || "—"}</td>
                  <td>{r.customer_part_number || "—"}</td>
                  <td>{r.origin_type || "—"}</td>
                  <td>{r.created_by_name || "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="cpq-fav-del"
                      disabled={deletingId === r.id}
                      title="Delete this saved configuration"
                      onClick={(e) => { e.stopPropagation(); del(r.id); }}
                    >
                      {deletingId === r.id ? "…" : "Delete"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
